import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  Box,
  Chip,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  BusinessRounded,
  GroupRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import ReportMetricCard from "components/reports/shared/ReportMetricCard";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import { getChartColors } from "theme";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";
import DashboardAllClients from "./DashboardAllClients";

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (value) =>
  new Intl.NumberFormat("en-US").format(Number(value || 0));

const KpiSkeleton = () => (
  <Grid container spacing={2} size={{ xs: 12 }}>
    {[0, 1].map((i) => (
      <Grid key={i} size={{ xs: 12, sm: 6 }}>
        <Skeleton variant="rounded" height={120} />
      </Grid>
    ))}
  </Grid>
);

// ─── main component ──────────────────────────────────────────────────────────

const DashboardV2AllClients = ({ user, dateSelection }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const chartColors = getChartColors(theme);
  const { refreshPage, handleErrorDialog } = useStateContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientsKPIs, setClientsKPIs] = useState(null);
  const DRAWER_INITIAL = { open: false, loading: false, title: "", subtitle: "", data: [], columns: [], summary: null, onRowClick: null };
  const [drillDown, setDrillDown] = useState(DRAWER_INITIAL);
  const closeDrillDown = () => setDrillDown(DRAWER_INITIAL);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get("/dashboard/all/clients/clientTotals")
      .then((res) => {
        setClientsKPIs(res?.data ?? null);
        setLoading(false);
      })
      .catch((err) => {
        console.error("[DashboardV2AllClients] clientTotals fetch failed", {
          status: err?.response?.status,
          data: err?.response?.data,
          message: err?.message,
        });
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Unable to load client summary. Please try again.";
        handleErrorDialog(msg, theme);
        setError(msg);
        setLoading(false);
      });
  }, [refreshPage, handleErrorDialog, theme]);

  // ── pie data ──────────────────────────────────────────────────────────────

  const byTypePie = useMemo(
    () => [
      { id: 0, value: Number(clientsKPIs?.byType?.Customer || 0), label: "Customer", color: chartColors.series[0] },
      { id: 1, value: Number(clientsKPIs?.byType?.Prospect || 0), label: "Prospect", color: chartColors.series[1] },
      { id: 2, value: Number(clientsKPIs?.byType?.Former || 0), label: "Former", color: chartColors.series[3] },
      { id: 3, value: Number(clientsKPIs?.byType?.["N/A"] || 0), label: "N/A", color: chartColors.series[5] },
    ],
    [clientsKPIs, chartColors],
  );

  const byServiceTypePie = useMemo(
    () => [
      { id: 0, value: Number(clientsKPIs?.byServiceType?.["OPI/VRI"] || 0), label: "OPI/VRI", color: chartColors.series[0] },
      { id: 1, value: Number(clientsKPIs?.byServiceType?.Translation || 0), label: "Translation", color: chartColors.series[1] },
      { id: 2, value: Number(clientsKPIs?.byServiceType?.["In-Person Interpretation"] || 0), label: "In-Person Interpretation", color: chartColors.series[2] },
      { id: 3, value: Number(clientsKPIs?.byServiceType?.Training || 0), label: "Training", color: chartColors.series[3] },
      { id: 4, value: Number(clientsKPIs?.byServiceType?.Consulting || 0), label: "Consulting", color: chartColors.series[5] },
      { id: 5, value: Number(clientsKPIs?.byServiceType?.["N/A"] || 0), label: "N/A", color: chartColors.series[6] },
    ],
    [clientsKPIs, chartColors],
  );

  const byContractStatusPie = useMemo(
    () => [
      { id: 0, value: Number(clientsKPIs?.byContractStatus?.Current || 0), label: "Current", color: theme.palette.success.main },
      { id: 1, value: Number(clientsKPIs?.byContractStatus?.["Needs Contract"] || 0), label: "Needs Contract", color: chartColors.series[3] },
      { id: 2, value: Number(clientsKPIs?.byContractStatus?.["N/A"] || 0), label: "N/A", color: chartColors.series[5] },
    ],
    [clientsKPIs, chartColors, theme],
  );

  const mapClientRows = (clients = []) => {
    return (clients || []).map((client) => {
      const latestContract = (client?.ClientContractHistories || []).find((c) => Number(c?.IsLatest) === 1) || (client?.ClientContractHistories || [])[0];
      return {
        id: client.id,
        account: client.Account || "-",
        shortName: client.ShortName || "-",
        type: client.Type || "N/A",
        serviceTypes: (client?.ClientServiceTypes || []).map((row) => row?.ServiceType?.Name).filter(Boolean).join(", ") || "N/A",
        contractStatus: latestContract?.Status || "N/A",
        address: client.MainAddress || "-",
      };
    });
  };

  const CLIENT_COLUMNS = [
    { field: "account", headerName: "Account", width: 220 },
    { field: "shortName", headerName: "Short Name", width: 150 },
    { field: "type", headerName: "Type", width: 120 },
    { field: "serviceTypes", headerName: "Service Types", width: 260 },
    { field: "contractStatus", headerName: "Contract", width: 150 },
    { field: "address", headerName: "Address", width: 260 },
  ];

  const REQUESTOR_COLUMNS = [
    { field: "name", headerName: "Requestor", width: 220 },
    { field: "email", headerName: "Email", width: 240 },
    { field: "phone", headerName: "Phone", width: 170 },
    { field: "client", headerName: "Client", width: 240 },
  ];

  const openClientProfileFromRow = (row) => {
    if (!row?.id) return;
    navigate(`/clients/${row.id}`, { state: { ComingFrom: "/dashboard" } });
  };

  const fetchAndOpenClientDrillDown = (meta, params = {}) => {
    setDrillDown({ ...DRAWER_INITIAL, open: true, loading: true, onRowClick: openClientProfileFromRow, ...meta });
    api
      .get("/dashboard/all/clients/clients", { params })
      .then((res) => {
        const rows = mapClientRows(res?.data || []);
        setDrillDown((prev) => ({ ...prev, loading: false, data: rows, columns: CLIENT_COLUMNS }));
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.response?.data?.message || "Unable to load drill-down data.";
        handleErrorDialog(msg, theme);
        setDrillDown((prev) => ({ ...prev, loading: false }));
      });
  };

  const fetchAndOpenRequestorDrillDown = () => {
    setDrillDown({
      ...DRAWER_INITIAL,
      open: true,
      loading: true,
      title: "Total Requestors",
      subtitle: "All clients",
      summary: { metrics: [{ label: "Total", value: fmt(clientsKPIs?.totalRequestors || 0), color: theme.palette.info.main }] },
      onRowClick: openClientProfileFromRow,
    });

    api
      .get("/dashboard/all/clients/clients")
      .then((res) => {
        const clients = res?.data || [];
        const rows = clients.flatMap((client) => {
          const requestors = client?.Requestors || [];
          return requestors.map((r) => ({
            id: client.id,
            name: r?.Name || "-",
            email: r?.Email || "-",
            phone: r?.PhoneNumber ? normalizePhoneNumber(r.PhoneNumber) : "-",
            client: client?.Account || client?.ShortName || "-",
          }));
        });
        setDrillDown((prev) => ({ ...prev, loading: false, data: rows, columns: REQUESTOR_COLUMNS }));
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.response?.data?.message || "Unable to load requestors.";
        handleErrorDialog(msg, theme);
        setDrillDown((prev) => ({ ...prev, loading: false }));
      });
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ pb: 3 }}>
      {/* V2 Analytics Summary */}
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Chip label="Client Analytics" size="small" color="primary" variant="outlined" />
          <Typography variant="body2" color="text.secondary">
            Client distribution and summary across the account
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* KPI cards */}
        {loading ? (
          <KpiSkeleton />
        ) : (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReportMetricCard
                title="Total Clients"
                value={fmt(clientsKPIs?.totalClients)}
                icon={<BusinessRounded />}
                color="primary"
                tooltipTitle="Total active client accounts"
                onClick={() =>
                  fetchAndOpenClientDrillDown(
                    {
                      title: "Total Clients",
                      subtitle: "All clients",
                      summary: { metrics: [{ label: "Total", value: fmt(clientsKPIs?.totalClients || 0), color: theme.palette.primary.main }] },
                    },
                    {},
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReportMetricCard
                title="Total Requestors"
                value={fmt(clientsKPIs?.totalRequestors)}
                icon={<GroupRounded />}
                color="info"
                tooltipTitle="Total requestor contacts across all clients"
                onClick={fetchAndOpenRequestorDrillDown}
              />
            </Grid>
          </Grid>
        )}

        {/* Distribution Pies */}
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Client Distribution</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <ReportPieChart
              title="By Client Type"
              data={byTypePie}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) =>
                fetchAndOpenClientDrillDown(
                  {
                    title: "By Client Type",
                    subtitle: `Selected: ${slice.label}`,
                    summary: { metrics: [{ label: "Count", value: fmt(slice.value), color: theme.palette.primary.main }] },
                  },
                  { category: "Type", value: slice.label },
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ReportPieChart
              title="By Service Type"
              data={byServiceTypePie}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) =>
                fetchAndOpenClientDrillDown(
                  {
                    title: "By Service Type",
                    subtitle: `Selected: ${slice.label}`,
                    summary: { metrics: [{ label: "Count", value: fmt(slice.value), color: theme.palette.primary.main }] },
                  },
                  { category: "ServiceType", value: slice.label },
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ReportPieChart
              title="By Contract Status"
              data={byContractStatusPie}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) =>
                fetchAndOpenClientDrillDown(
                  {
                    title: "By Contract Status",
                    subtitle: `Selected: ${slice.label}`,
                    summary: { metrics: [{ label: "Count", value: fmt(slice.value), color: theme.palette.primary.main }] },
                  },
                  { category: "Contract", value: slice.label },
                )
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Full Client Directory */}
      <Box sx={{ px: 1 }}>
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Client Directory</Typography>
      </Box>
      <DashboardAllClients user={user} compactLayout />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
        onRowClick={drillDown.onRowClick}
        loading={drillDown.loading}
      />
    </Box>
  );
};

export default DashboardV2AllClients;
